import Debug from 'debug';
import { z } from 'zod';
import { CONNECTOR_END_STYLE } from '../constants';
import { ConnectorRouting } from '../objects/ConnectorRouting';
import { sanitizeShapeName, Shapes } from '../shapes';
import { regularSanitizer } from './html';
import { normalizeVerticalAlign } from './verticalAlign';

const debug = Debug('whiteboard:assistant');

/**
 * Regex for validating CSS colors in the following formats:
 * - Hex: #RRGGBB
 * - Hex: #RGB
 * - Hex: #fff
 * - RGBA: rgba(r,g,b,a)
 * - RGB: rgb(r,g,b)
 */
export const cssColorRegex = new RegExp(
  `^(?:#[0-9A-F]{6}` +
    `|#[0-9a-f]{6}` +
    `|#[0-9A-F]{3}` +
    `|#[0-9a-f]{3}` +
    `|rgba\\(\\s*(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\s*,\\s*){3}(?:0|1|0?\\.[0-9]+)\\s*\\)` +
    `|rgb\\(\\s*(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\s*,\\s*){2}(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\s*)\\)` +
    `|transparent` +
    `|)$`,
  'i'
);

const cssColorSchema = z
  .string()
  .regex(
    cssColorRegex,
    'Must be a valid CSS color: #RRGGBB, rgba(r,g,b,a), or rgb(r,g,b)'
  )
  .catch('#000000')
  .optional()
  .nullable();

/** number schema that accepts and transforms strings too, if they can be parsed as numbers */
const ForgivingNumber = z
  .union([z.number(), z.string()])
  .transform((value, ctx) => {
    if (typeof value === 'number') {
      return value;
    }
    const parsed = Number(value);
    if (isNaN(parsed) || !isFinite(parsed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Cannot parse ${value} as a number`,
      });
      return z.NEVER;
    }
    return parsed;
  });

const angleSchema = ForgivingNumber.default(0).transform(value => {
  // convert whatever we end up with here to its equivalent positive angle
  return ((value % 360) + 360) % 360;
});

// Remap icons from AI to whiteboard
const iconMapping: Record<string, string> = {
  search: 'magnifying-glass',
  cog: 'gear',
  'question-circle': 'circle-question',
  'user-circle': 'circle-user',
  'user-circle-alt': 'circle-user',
  'exclamation-circle': 'circle-exclamation',
  'check-square': 'square-check',
  'check-circle': 'circle-check',
  'times-circle': 'circle-xmark',
};

const iconNameSchema = z
  .string()
  .optional()
  .transform(value => {
    if (value) {
      // strip -alt from the end of the icon name
      value = value.replace(/-alt$/, '');

      if (value.startsWith('fas/')) {
        value = value.replace('fas/', '');
        return `fas/${iconMapping[value] || value}`;
      }

      return iconMapping[value] || value;
    }
    return value;
  });

const VerticalAlignSchema = z
  .enum(['flex-start', 'middle', 'flex-end', 'top', 'center', 'bottom'])
  .transform(normalizeVerticalAlign)
  .optional();

// Common properties
const commonProperties = {
  id: z.string(),
  z: z.number().int().optional(),
  container: z.string().optional().nullable(),
};

// Some properties are named differently in the AI response compared to the whiteboard
// Because the AI has predefined ideas of what properties should be called and what they should do
// so we give them better names for the AI to understand their purpose
const transformalProperties = {
  selectedItemTextColor: 'fill',
  inputBorderColor: 'fill',
  breadcrumbTextColor: 'fill',
  paginationTextColor: 'fill',
  checkboxColor: 'fill',
  radioButtonColor: 'fill',
  multiSelectColor: 'fill',
  multiSelectSelectedItemTextColor: 'textColor',
  pillColor: 'fill',
};

// Take incoming properties from the AI and map them to the names we use for the whiteboard e.g. selectedItemFill from AI -> fill for whiteboard use
// Should be called in a preprocess step for each discriminated union schema
// const DiagramShapeSchema = z.preprocess(
//   transformProperties,
//   z.discriminatedUnion('shapeName', [StandardShapes, NoteSchema, IconSchema])
// );
function transformProperties(obj: Object) {
  for (const [key, value] of Object.entries(obj)) {
    if (key in transformalProperties) {
      obj[transformalProperties[key]] = value;
      delete obj[key];
    }
  }
  return obj;
}

const allShapeNames = Object.keys(Shapes) as (keyof typeof Shapes)[];

// Point schema for connector - now using object with x,y properties
const PointSchema = z.object({
  x: ForgivingNumber,
  y: ForgivingNumber,
});

// Connector arrowhead schema
const endStyleSchema = z.union([
  z.literal(CONNECTOR_END_STYLE.NONE),
  z.literal(CONNECTOR_END_STYLE.POINTY_ARROW),
  z.literal(CONNECTOR_END_STYLE.FILLED_ARROW),
  z.literal(CONNECTOR_END_STYLE.ARROW),
  z.literal(CONNECTOR_END_STYLE.FILLED_CIRCLE),
  z.literal(CONNECTOR_END_STYLE.CIRCLE),
  z.literal(CONNECTOR_END_STYLE.DIAMOND),
  z.literal(CONNECTOR_END_STYLE.SLASH),
]);

const BaseShapeSchema = z.object({
  type: z.literal('shape'),
  ...commonProperties,
  textContent: z.string().optional(),

  // Sudo properties applied in postParse
  textColor: cssColorSchema,
  fontWeight: z.enum(['normal', 'bold']).optional(),
  fontStyle: z.enum(['normal', 'italic']).optional(),
  textDecoration: z.enum(['none', 'underline', 'line-through']).optional(),

  left: ForgivingNumber.optional(),
  top: ForgivingNumber.optional(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  scaleX: ForgivingNumber.default(1).optional(),
  scaleY: ForgivingNumber.default(1).optional(),
  stroke: cssColorSchema,
  strokeWidth: z.number().nonnegative().optional(),
  opacity: z.number().min(0).max(1).optional(),
  borderOpacity: z.number().min(0).max(1).optional(),
  fill: cssColorSchema,
  textOpacity: z.number().min(0).max(1).optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  textMargin: ForgivingNumber.optional(),
  verticalAlign: VerticalAlignSchema,
  fontSize: z.number().positive().default(14).optional(),
  angle: angleSchema,
  iconName: iconNameSchema,
  wrapText: z.boolean().nullable().default(true),

  // connector label properties
  connectorId: z.string().optional(),
  labelPosition: ForgivingNumber.optional(),
  labelOrientation: z.literal('inline').optional(),
});

const StandardShapes = BaseShapeSchema.extend({
  shapeName: z.enum(
    allShapeNames.filter(
      name =>
        ![
          'customPath',
          'icon',
          'milestone',
          'note',
          'progressBar',
          'record',
        ].includes(name)
    ) as [string, ...string[]]
  ),
});

const NoteSchema = BaseShapeSchema.omit({ stroke: true }).extend({
  shapeName: z.literal('note'),
});

const IconSchema = BaseShapeSchema.extend({
  shapeName: z.literal('icon'),
  iconName: iconNameSchema,
});

const RecordSchema = BaseShapeSchema.extend({
  shapeName: z.literal('record'),
  recordType: z.string(),
  recordId: z.string(),
});

const ProgressBarSchema = BaseShapeSchema.extend({
  shapeName: z.literal('progressBar'),
  textLocation: z
    .enum(['above', 'inside', 'below', 'left', 'right'])
    .optional(),
  progress: z.number().min(0).max(1).optional(),
  variation: z
    .enum(['rounded', 'pointRight', 'pointBoth', 'pointRightBoth'])
    .optional(),
});

const MilestoneSchema = BaseShapeSchema.extend({
  shapeName: z.literal('milestone'),
  variation: z.enum(['diamond', 'disc', 'rightTriangle', 'star']).optional(),
  textLocation: z
    .enum(['above', 'inside', 'below', 'left', 'right'])
    .optional(),
});

const CustomPathSchema = BaseShapeSchema.extend({
  shapeName: z.literal('customPath'),
  shapePath: z.string(),
});

const DiagramShapeSchema = z.preprocess(
  transformProperties,
  z.discriminatedUnion('shapeName', [
    StandardShapes,
    NoteSchema,
    IconSchema,
    RecordSchema,
    ProgressBarSchema,
    MilestoneSchema,
    CustomPathSchema,
  ])
);

// Connector schema
const ConnectorSchema = z.object({
  type: z.literal('connector'),
  ...commonProperties,
  stroke: cssColorSchema,
  strokeWidth: z.number().nonnegative().optional(),
  lineRouting: z.union([
    z.literal(ConnectorRouting.ROUTING_STRAIGHT),
    z.literal(ConnectorRouting.ROUTING_BEZIER),
    z.literal(ConnectorRouting.ROUTING_ORTHOGONAL_AUTO),
    z.literal(ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL),
  ]),
  points: z.array(PointSchema).min(2).optional(),
  alignLabels: z.boolean().optional(),
  fromId: z.string().optional(),
  toId: z.string().optional(),
  endStyles: z
    .tuple([endStyleSchema, endStyleSchema])
    .default([0, 20])
    .optional(),
});

// Group schema
const GroupSchema = z.object({
  type: z.literal('group'),
  ...commonProperties,
  objects: z.array(z.string()).optional(), // Array of object IDs
});

// Create a schema for path commands
const PathCommandSchema = z.union([
  // Move command: ["M", x, y]
  z.tuple([z.literal('M'), z.number(), z.number()]),

  // Quadratic curve command: ["Q", controlX, controlY, endX, endY]
  z.tuple([
    z.literal('Q'),
    z.number(), // control point x
    z.number(), // control point y
    z.number(), // end point x
    z.number(), // end point y
  ]),

  // Line command: ["L", x, y]
  z.tuple([z.literal('L'), z.number(), z.number()]),
]);

// Path data schema requires an array of commands starting with M and ending with L
const PathDataSchema = z
  .array(PathCommandSchema)
  .refine(
    commands => commands.length >= 2, // Minimum 2 commands needed
    { message: 'Path must have at least 2 commands' }
  )
  .refine(commands => commands[0][0] === 'M', {
    message: 'Path must start with a Move (M) command',
  });

// Path schema as a top-level type
const PathSchema = z.object({
  type: z.literal('path'),
  ...commonProperties,
  left: ForgivingNumber.optional(),
  top: ForgivingNumber.optional(),
  angle: angleSchema,
  scaleX: ForgivingNumber.default(1).optional(),
  scaleY: ForgivingNumber.default(1).optional(),
  stroke: cssColorSchema,
  strokeWidth: z.number().nonnegative().optional(),
  path: PathDataSchema,
});

const validEmojis = [
  'aha-icon--thumbs-up',
  'aha-icon--thumbs-down',
  'aha-icon--question',
  'aha-icon--star',
  'aha-icon--slight-smile',
  'aha-icon--frowning',
  'aha-icon--smile',
  'aha-icon--disappointed',
  'aha-icon--heart-eyes',
  'aha-icon--confused',
  'aha-icon--star-struck',
  'aha-icon--astonished',
  'aha-icon--slight-smile-elle',
  'aha-icon--frowning-elle',
  'aha-icon--smile-elle',
  'aha-icon--disappointed-elle',
  'aha-icon--heart-eyes-elle',
  'aha-icon--confused-elle',
  'aha-icon--star-struck-elle',
  'aha-icon--astonished-elle',
] as const;

const StampSchema = z.object({
  type: z.literal('svgStamp'),
  ...commonProperties,
  left: ForgivingNumber,
  top: ForgivingNumber,
  scaleX: z.number().positive().default(1),
  scaleY: z.number().positive().default(1),
  angle: angleSchema,
  svg: z.enum(validEmojis),
});

const FrameSchema = z.object({
  type: z.literal('frame'),
  ...commonProperties,
  left: ForgivingNumber,
  top: ForgivingNumber,
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  scaleX: z.number().positive().default(1),
  scaleY: z.number().positive().default(1),
  angle: angleSchema,
  opacity: z.number().min(0).max(1).optional(),
  fill: cssColorSchema,
  layoutEnabled: z.boolean().optional(),
  layoutSizeHorizontal: z.enum(['children', 'parent', 'user']).optional(),
  layoutSizeVertical: z.enum(['children', 'parent', 'user']).optional(),
  layoutGap: z.union([ForgivingNumber, z.literal('auto')]).optional(),
  layoutPadding: ForgivingNumber.optional(),
  layoutDirection: z.enum(['horizontal', 'vertical', 'wrap']).optional(),
  layoutAlignHorizontal: z.enum(['left', 'center', 'right']).optional(),
  layoutAlignVertical: z.enum(['top', 'center', 'bottom']).optional(),
});

const DeviceSchema = z.object({
  type: z.literal('device'),
  deviceName: z.enum(['laptop', 'tablet', 'phone', 'browser']),
  ...commonProperties,
  left: ForgivingNumber,
  top: ForgivingNumber,
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  scaleX: z.number().positive().default(1),
  scaleY: z.number().positive().default(1),
  opacity: z.number().min(0).max(1).optional(),
  fill: cssColorSchema,
  orientation: z.enum(['portrait', 'landscape']),
  showFramed: z.boolean().optional(),
});

const CellTextContentsSchema = z.object({
  fontSize: z.number().positive().default(14),
  textAlign: z.enum(['left', 'center', 'right']).default('left'),
  textContent: z.string().default(''),
  textOpacity: z.number().min(0).max(1).optional(),
  verticalAlign: VerticalAlignSchema,
});

const TableTextContentsSchema = z.record(
  z.string().regex(/^cell_\d+_\d+$/),
  CellTextContentsSchema.nullable()
);

const BaseTableSchema = z.object({
  type: z.literal('table'),
  ...commonProperties, // id, z, container
  left: ForgivingNumber,
  top: ForgivingNumber,
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  fill: cssColorSchema,
  stroke: cssColorSchema,
  strokeWidth: z.number().positive().optional(),
  opacity: z.number().min(0).max(1).optional(),
  numRows: z.number().positive(),
  numColumns: z.number().positive(),
  textFields: TableTextContentsSchema.optional(),
});

// We have lots of dynamic keys (eg row_10_height) which zod does not play nicely
// with. The only way to handle these is have the BaseTableSchema
// introduce whatever errors it wants, then pass every field through. We then
// manually check all the dynamic keys as well as stripping out any keys that
// shouldn't be allowed.
const TableSchema = BaseTableSchema.passthrough().superRefine((data, ctx) => {
  // Match only the already defined keys and z.string().regex(/^row_\d+_height$/) for the rest
  const keys = Object.keys(data);
  const validKeys = Object.keys(BaseTableSchema.shape);
  for (const key of keys) {
    if (validKeys.includes(key)) {
      // Allowed key;
    } else if (key.match(/^row_\d+_height$/) || key.match(/^col_\d+_width$/)) {
      if (typeof data[key] !== 'number') {
        ctx.addIssue({
          path: [key],
          message: 'Must be a number',
          code: z.ZodIssueCode.custom,
        });
      }
    } else if (key.match(/cell_\d+_\d+_fill$/)) {
      if (typeof data[key] !== 'string' || !data[key].match(cssColorRegex)) {
        ctx.addIssue({
          path: [key],
          message: 'Must be a css color string',
          code: z.ZodIssueCode.custom,
        });
      }
    } else if (key.match(/cell_\d+_\d+_opacity$/)) {
      if (typeof data[key] !== 'number') {
        ctx.addIssue({
          path: [key],
          message: 'Must be a number',
          code: z.ZodIssueCode.custom,
        });
      }
    } else {
      delete data[key];
    }
  }
});

const ItemIcon = z.object({
  prefix: z.string(),
  iconName: iconNameSchema,
});

const PlaceholderItem = z.object({
  value: z.string().optional(),
  position: z
    .enum([
      'noLabel',
      'inside',
      'outside',
      'left',
      'right',
      'text',
      'textAndValue',
      'placeholder',
    ])
    .optional(),
  iconRight: ItemIcon.optional(),
  iconLeft: ItemIcon.optional(),
});

const Item = z
  .object({
    value: z.string().optional(),
    id: z.string(),
    item_position: ForgivingNumber.optional(),
    state: z.enum(['default', 'disabled', 'selected']).optional(),
    iconLeft: ItemIcon.optional(),
    iconRight: ItemIcon.optional(),
  })
  .optional();

const ItemData = z.record(z.string(), Item).optional();

const commonWireframeProperties = {
  type: z.literal('wireframe'),
  ...commonProperties,
  left: ForgivingNumber,
  top: ForgivingNumber,
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  scaleX: z.number().positive().default(1),
  scaleY: z.number().positive().default(1),
  stroke: cssColorSchema,
  strokeWidth: z.number().nonnegative().optional(),
  opacity: z.number().min(0).max(1).optional(),
  fill: cssColorSchema,
  textColor: cssColorSchema,
  fontSize: z.number().positive().default(14).optional(),
  setDimension: z.boolean().optional(),
};

const ButtonWireframeSchema = z.object({
  wireframeName: z.literal('button'),
  ...commonWireframeProperties,
  style: z
    .enum(['round', 'square', 'link', 'roundFilled', 'squareFilled'])
    .optional(),
  uiState: z.enum(['default', 'disabled']).optional(),
  placeholder: PlaceholderItem.optional(),
});

const ToggleWireframeSchema = z.object({
  wireframeName: z.enum(['toggle']),
  ...commonWireframeProperties,
  style: z.enum(['round', 'square', 'roundFilled', 'squareFilled']).optional(),
  uiState: z.enum(['default', 'disabled']).optional(),
  componentState: z.enum(['on', 'off']).optional(),
  label: PlaceholderItem.optional().default({
    value: 'Label',
    position: 'noLabel',
  }),
});

const LinkWireframeSchema = z.object({
  wireframeName: z.literal('link'),
  ...commonWireframeProperties,
  style: z
    .enum(['link', 'round', 'square', 'roundFilled', 'squareFilled'])
    .optional(),
  placeholder: PlaceholderItem.optional(),
});

const CheckboxWireframeSchema = z.object({
  wireframeName: z.literal('checkbox'),
  ...commonWireframeProperties,
  uiState: z.enum(['default', 'disabled']).optional(),
  label: PlaceholderItem.optional().default({
    value: 'Label',
    position: 'noLabel',
  }),
  componentState: z.enum(['checkbox', 'empty', 'dash']).optional(),
});

const RadioButtonWireframeSchema = z.object({
  wireframeName: z.literal('radioButton'),
  ...commonWireframeProperties,
  uiState: z.enum(['default', 'disabled']).optional(),
  label: PlaceholderItem.optional().default({
    value: 'Label',
    position: 'noLabel',
  }),
  componentState: z.enum(['empty', 'filled']).optional(),
});

const MultiSelectWireframeSchema = z.object({
  wireframeName: z.literal('multiSelect'),
  ...commonWireframeProperties,
  style: z.enum(['round', 'square']).optional(),
  itemsData: ItemData.optional(),
  showIcons: z.boolean().optional(),
});

const SliderWireframeSchema = z.object({
  wireframeName: z.literal('slider'),
  ...commonWireframeProperties,
  label: PlaceholderItem.optional().default({
    value: 'Label',
    position: 'noLabel',
  }),
  progress: z.number().min(0).max(1).optional(),
});

const PillWireframeSchema = z.object({
  wireframeName: z.literal('pill'),
  ...commonWireframeProperties,
  style: z.enum(['round', 'square']).optional(),
  showBackground: z.boolean().optional(),
  showOutline: z.boolean().optional(),
  placeholder: PlaceholderItem.optional(),
});

const NavMenuVerticalWireframeSchema = z.object({
  wireframeName: z.literal('navMenuVertical'),
  ...commonWireframeProperties,
  style: z.enum(['round', 'square']).optional(),
  itemsData: ItemData,
  layout: z.enum(['column']).optional(),
  showIcons: z.boolean().optional(),
  showSeparators: z.boolean().optional(),
});

const NavMenuHorizontalWireframeSchema = z.object({
  wireframeName: z.literal('navMenuHorizontal'),
  ...commonWireframeProperties,
  style: z.enum(['round', 'square']).optional(),
  itemsData: ItemData,
  layout: z.enum(['row']).optional(),
  showIcons: z.boolean().optional(),
  showSeparators: z.boolean().optional(),
});

const DropdownWireframeSchema = z.object({
  wireframeName: z.literal('dropdown'),
  ...commonWireframeProperties,
  style: z
    .enum(['underlined', 'framed', 'underlinedWithPanel', 'framedWithPanel'])
    .optional(),
  itemsData: ItemData.optional(),
  label: PlaceholderItem.optional().default({
    value: 'Label',
    position: 'noLabel',
  }),
  placeholder: PlaceholderItem.optional(),
});

const ScrollBarWireframeSchema = z.object({
  wireframeName: z.literal('scrollBar'),
  ...commonWireframeProperties,
  style: z
    .enum([
      'vertical_round',
      'vertical_square',
      'horizontal_round',
      'horizontal_square',
    ])
    .optional(),
  progress: z.number().min(0).max(1).optional(),
  showOutline: z.boolean().optional(),
});

const BreadcrumbWireframeSchema = z.object({
  wireframeName: z.literal('breadcrumb'),
  ...commonWireframeProperties,
  itemsData: ItemData.optional(),
  style: z.enum(['slash', 'angle']).optional(),
  showIcons: z.boolean().optional(),
  showSeparators: z.boolean().optional(),
});

const PaginationWireframeSchema = z.object({
  wireframeName: z.literal('pagination'),
  ...commonWireframeProperties,
  style: z.enum(['framed', 'unframed']).optional(),
  itemsData: ItemData.optional(),
  buttonLeft: z.string().optional(),
  buttonRight: z.string().optional(),
  showIcons: z.boolean().optional(),
  showButtonText: z.boolean().optional(),
  showEllipsis: z.boolean().optional(),
});

const InputFieldWireframeSchema = z.object({
  wireframeName: z.literal('inputField'),
  ...commonWireframeProperties,
  style: z
    .enum(['underlined', 'framed', 'underlinedWithPanel', 'framedWithPanel'])
    .optional(),
  placeholder: PlaceholderItem.optional(),
  label: PlaceholderItem.optional().default({
    value: 'Label',
    position: 'noLabel',
  }),
});

const DatePickerWireframeSchema = z.object({
  wireframeName: z.literal('datePicker'),
  ...commonWireframeProperties,
  style: z
    .enum(['underlined', 'framed', 'underlinedWithPanel', 'framedWithPanel'])
    .optional(),
  placeholder: PlaceholderItem.optional(),
  label: PlaceholderItem.optional().default({
    value: 'Label',
    position: 'noLabel',
  }),
});

const SearchBarWireframeSchema = z.object({
  wireframeName: z.literal('searchBar'),
  ...commonWireframeProperties,
  placeholder: PlaceholderItem.optional(),
  style: z
    .enum(['underlined', 'framed', 'underlinedWithPanel', 'framedWithPanel'])
    .optional(),
  itemsData: ItemData.optional(),
});

const ImageWireframeSchema = z.object({
  wireframeName: z.enum([
    'image',
    'pieChart',
    'barChart',
    'lineChart',
    'donutChart',
    'video',
    'map',
  ]),
  ...commonWireframeProperties,
  style: z.literal('image').default('image'),
});

const AvatarWireframeSchema = z.object({
  wireframeName: z.literal('avatar'),
  ...commonWireframeProperties,
  style: z.enum(['round', 'square']).optional(),
  placeholder: PlaceholderItem.optional(),
  showBackground: z.boolean().optional(),
  showOutline: z.boolean().optional(),
});

const WireframeSchema = z.preprocess(
  transformProperties,
  z.discriminatedUnion('wireframeName', [
    ButtonWireframeSchema,
    ToggleWireframeSchema,
    LinkWireframeSchema,
    CheckboxWireframeSchema,
    RadioButtonWireframeSchema,
    MultiSelectWireframeSchema,
    SliderWireframeSchema,
    PillWireframeSchema,
    NavMenuVerticalWireframeSchema,
    NavMenuHorizontalWireframeSchema,
    DropdownWireframeSchema,
    ScrollBarWireframeSchema,
    BreadcrumbWireframeSchema,
    PaginationWireframeSchema,
    InputFieldWireframeSchema,
    DatePickerWireframeSchema,
    SearchBarWireframeSchema,
    ImageWireframeSchema,
    AvatarWireframeSchema,
  ])
);

// Combined schema for all diagram objects
const DiagramNonShapeSchema = z.preprocess(
  transformProperties,
  z.discriminatedUnion('type', [
    ConnectorSchema,
    GroupSchema,
    PathSchema,
    StampSchema,
    FrameSchema,
    DeviceSchema,
  ])
);

interface ParseDataResponse {
  success: boolean;
  data?: any;
  error?: any;
}

// Zod had the idea of a discriminatedUnion which allows it to select the schema
// to match against based on a key value. This improves performance and makes
// the returned errors much clearer compared to using a straight union. Zod does
// not however have a way to deal with two discriminate keys at the same level.
// So it cannot deal with out type and shapeName at the same level. To get
// around that, do a little bit of manual direction before passing off to Zod.
export function parseData(jsonData: unknown): ParseDataResponse {
  if ((jsonData as { type: string }).type === 'shape') {
    return DiagramShapeSchema.safeParse(jsonData);
  } else if ((jsonData as { wireframeName: string }).wireframeName) {
    return WireframeSchema.safeParse(jsonData);
  } else if ((jsonData as { type: string }).type === 'table') {
    return TableSchema.safeParse(jsonData);
  } else {
    return DiagramNonShapeSchema.safeParse(jsonData);
  }
}

// Combined schema for all possible whiteboard objects
export const WhiteboardObjectSchema = z.union([
  DiagramShapeSchema,
  WireframeSchema,
  TableSchema,
  DiagramNonShapeSchema,
]);

export type WhiteboardObject = z.infer<typeof WhiteboardObjectSchema>;

// Validate a single object and return a valid and clean version of the same object.
export function validateAIResponse(jsonData: unknown): {
  success: boolean;
  data?: WhiteboardObject;
  errors?: any;
} {
  let data: unknown;

  try {
    // If input is a string, parse it first
    data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
  } catch (error) {
    if (error instanceof SyntaxError) {
      return {
        success: false,
        errors: [{ message: 'Invalid JSON format' }],
      };
    } else {
      throw error;
    }
  }

  const validatedData = parseData(data);

  if (!validatedData.success) {
    debug('JSON validation failed:', validatedData);

    return {
      success: false,
      errors: validatedData.error,
    };
  }

  const whiteboardData = validatedData.data as WhiteboardObject;
  postParse(whiteboardData);

  return {
    success: true,
    data: whiteboardData,
  };
}

export function postParse(data: any) {
  if (data.type === 'shape' && data.shapeName === 'icon') {
    // Icons are always 100x100, so if width and height arent set, set them to 100 and scale the icon.
    applyFixedSize(data, 100, 100);
  }

  // stickies must be either 100x100 or 100x166, or the whiteboard will reset them
  if (data.type === 'shape' && data.shapeName === 'note') {
    applyFixedSize(data, data.width > data.height * 1.33 ? 166 : 100, 100);
  }

  if (data.type === 'wireframe' && data.wireframeName === 'avatar') {
    // Avatars are always 50x50, so if width and height arent set, set them to 50 and scale the icon.
    applyFixedSize(data, 50, 50, true);
  }

  if (data.type === 'device') {
    if (data.deviceName === 'browser') {
      // Browser are always 1264x920, so if width and height arent set, set them to 50 and scale the icon.
      applyFixedSize(data, 1264, 920);
    } else if (data.deviceName === 'phone') {
      // 300x645
      applyFixedSize(data, 300, 645);
    } else if (data.deviceName === 'tablet') {
      // 600x800
      applyFixedSize(data, 600, 800);
    } else if (data.deviceName === 'laptop') {
      // 1264x920
      applyFixedSize(data, 1264, 920);
    }
  }

  if (
    data.type === 'wireframe' &&
    (data.wireframeName === 'searchBar' ||
      data.wireframeName === 'inputField' ||
      data.wireframeName === 'datePicker' ||
      data.wireframeName === 'dropdown')
  ) {
    if (data.label && data.label.position === 'noLabel') {
      applyFixedSize(data, 240, 40, true);
    } else {
      applyFixedSize(data, 240, 65, true);
    }
  }

  if (data.type === 'wireframe' && data.wireframeName === 'slider') {
    if (data.label && data.label.position === 'noLabel') {
      applyFixedSize(data, 240, 42, true);
    } else {
      applyFixedSize(data, 200, 58, true);
    }
  }

  // Apply fontWeight, fontStyle, and textColor to textContent as html
  if (data.textContent) {
    if (data.fontWeight === 'bold') {
      data.textContent = `<strong>${data.textContent}</strong>`;
    }
    if (data.fontStyle === 'italic') {
      data.textContent = `<em>${data.textContent}</em>`;
    }

    if (data.textDecoration === 'underline') {
      data.textContent = `<u>${data.textContent}</u>`;
    } else if (data.textDecoration === 'line-through') {
      data.textContent = `<s>${data.textContent}</s>`;
    }

    if (data.textColor) {
      data.textContent = `<span style="color: ${data.textColor};">${data.textContent}</span>`;
    }

    // Sanitize HTML including stripping images out of textContent since they are a data exfiltration vector.
    data.textContent = regularSanitizer(data.textContent);
  }

  if (data.type === 'shape' && data.shapeName === 'text') {
    // It consistently draws logos too small, so the text wraps looking bad
    if (data.width && data.id === 'logo') {
      data.width += 40;
    }
  }
}

function applyFixedSize(
  data: any,
  fixedWidth: number,
  fixedHeight: number,
  sameRatio = false
) {
  let scaleX = (data.width * (data.scaleX || 1)) / fixedWidth || 1;
  let scaleY = (data.height * (data.scaleY || 1)) / fixedHeight || 1;

  if (sameRatio) {
    scaleX = Math.min(scaleX, scaleY);
    scaleY = scaleX;
  }

  if (data.width && data.width !== fixedWidth) {
    data.width = fixedWidth;
    data.scaleX = scaleX;
  }
  if (data.height && data.height !== fixedHeight) {
    data.height = fixedHeight;
    data.scaleY = scaleY;
  }

  if (!data.width && data.scaleX) {
    data.width = fixedWidth * data.scaleX;
  }

  if (!data.height && data.scaleY) {
    data.height = fixedHeight * data.scaleY;
  }
}

function _mapWireframeToShape(jsonData: Record<string, unknown>) {
  // whiteboard:assistant Received change create with value {"id":"feature2","type":"wireframe","wireframeName":"icon","left":440,"top":500,"width":50,"height":50,"z":3,"scaleX":1,"scaleY":1,"placeholder":{"iconLeft":{"prefix":"fas","iconName":"comments"}},"fill":"#FF6600","container":"device1"} +2s

  const schema = z.object({
    type: z.enum(['wireframe']),
    wireframeName: z.enum(['text', 'icon', 'note', 'rect']),
    placeholder: z
      .object({
        iconLeft: z.object({ prefix: z.string(), iconName: z.string() }),
      })
      .optional(),
    iconName: z.string().optional(),
  });

  const { data, success } = schema.safeParse(jsonData);
  if (success) {
    const iconName =
      data.wireframeName === 'icon' &&
      data.placeholder?.iconLeft.prefix &&
      data.placeholder.iconLeft.iconName
        ? `${data.placeholder.iconLeft.prefix}/${data.placeholder.iconLeft.iconName}`
        : data.iconName;
    return {
      ...jsonData,
      type: 'shape',
      shapeName: data.wireframeName,
      wireframeName: undefined,
      iconName,
      placeHolder: iconName ? undefined : data.placeholder,
    } as const;
  }
}

// Shape name aliases - map common AI-provided names to our shape names
export const shapeNameAliases: Record<string, string> = {
  ellipse: 'circle',
  oval: 'circle',
  rectangle: 'rect',
  box: 'rect',
  square: 'rect',
} as const;

interface SanitisedShapeData {
  type: string; // TODO type as valid object types

  [k: string]: unknown;
}

const isObject = (obj: unknown): obj is Record<string, unknown> =>
  typeof obj === 'object' && obj !== null && !Array.isArray(obj);

const sanitizeConnectorData = (
  jsonData: Record<string, unknown>
): SanitisedShapeData => {
  const sanitized: SanitisedShapeData = { ...jsonData, type: 'connector' };

  // Map endStyles from string values provided by LLM. Fallback to 0 for start
  // and 20 for end if values are invalid.
  if (sanitized.endStyles && Array.isArray(sanitized.endStyles)) {
    sanitized.endStyles = sanitized.endStyles.map((style, i) => {
      let endStyle;

      if (typeof style === 'string') {
        endStyle =
          CONNECTOR_END_STYLE[style.toUpperCase().replace(/\s+/g, '_')];
      }

      // LLM value was invalid, default to none for start and arrow for end
      if (!endStyle) {
        return i === 0
          ? CONNECTOR_END_STYLE.NONE
          : CONNECTOR_END_STYLE.FILLED_ARROW;
      }

      return endStyle;
    });
  }

  // Currently, the Connector class expects to always have points defined. If/when
  // we change the connector code to accept points || attachments, we can remove this.
  if (
    !sanitized.points ||
    !Array.isArray(sanitized.points) ||
    sanitized.points.length < 2
  ) {
    sanitized.points = [
      { x: 0, y: 0 },
      { x: 100, y: 100 },
    ];
  }

  return sanitized;
};

/** Take shape data produced by the LLM and return a sanitised version that we know
 * can be used to create a shape, or null if it's malformed */
export function sanitizeShapeData(
  jsonData: unknown
): SanitisedShapeData | null {
  if (!isObject(jsonData)) {
    return null;
  }

  let sanitized: Record<string, unknown> = { ...jsonData };

  // if it's a wireframe that should be a shape, convert it
  const mappedToShape = _mapWireframeToShape(jsonData);
  if (mappedToShape) {
    sanitized = mappedToShape;
  }

  // frames and tables are not wireframes
  if (
    sanitized.type === 'wireframe' &&
    ['frame', 'table'].includes(sanitized.wireframeName as string)
  ) {
    sanitized.type = sanitized.wireframeName;
    delete sanitized.wireframeName;
  }

  if (sanitized.type === 'connector') {
    sanitized = sanitizeConnectorData(sanitized);
  }

  if (sanitized.type === 'shape') {
    const alias = shapeNameAliases[sanitized.shapeName as string];
    sanitized.shapeName =
      alias ?? sanitizeShapeName(sanitized.shapeName as string);
  }

  // Likes to just put text type instead of shape
  if (sanitized.type === 'text') {
    sanitized.type = 'shape';
    sanitized.shapeName = 'text';
  }

  // ensure text without a width (e.g. connector labels) doesn't wrap
  if (sanitized.shapeName === 'text' && !sanitized.width) {
    sanitized.wrapText = false;
  }

  return {
    type: 'shape',
    ...sanitized,
  };
}

/** @deprecated use sanitizeShapeData */
export function mapShapeToAnother(jsonData: Record<string, unknown>) {
  const mappedToShape = _mapWireframeToShape(jsonData);
  if (mappedToShape) {
    return mappedToShape;
  }

  if (jsonData.type === 'wireframe' && jsonData.wireframeName === 'frame') {
    return {
      ...jsonData,
      type: 'frame',
      wireframeName: undefined,
    };
  }

  // Likes to just put text type instead of shape	  if (jsonData.type === 'wireframe' && jsonData.wireframeName === 'frame') {
  // {"id":"calendarTitle","type":"text","shapeName":"text","textContent":"<span style=\"color: #FFFFFF; font-size: 16px;\">Google Calendar</span>","left":452,"top":460,"width":150,"height":20,"z":1050,"textAlign":"left","container":"device1"}
  if (jsonData.type === 'text' && jsonData.shapeName === 'text') {
    return { ...jsonData, type: 'shape' };
  }

  // whiteboard:assistant Received change create with value {"id":"leaderboard1","type":"wireframe","wireframeName":"table","left":276,"top":314,"width":468,"height":200,"container":"7371782949829060430","z":1070,"numColumns":3,"numRows":5,"textFields":{"cell_0_0":{"textContent":"Rank","textAlign":"center","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_0_1":{"textContent":"Rider","textAlign":"center","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_0_2":{"textContent":"Miles","textAlign":"center","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_1_0":{"textContent":"1","textAlign":"center","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_1_1":{"textContent":"John D.","textAlign":"left","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_1_2":{"textContent":"150","textAlign":"right","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_2_0":{"textContent":"2","textAlign":"center","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_2_1":{"textContent":"Sarah M.","textAlign":"left","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_2_2":{"textContent":"132","textAlign":"right","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_3_0":{"textContent":"3","textAlign":"center","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_3_1":{"textContent":"Alex K.","textAlign":"left","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_3_2":{"textContent":"128","textAlign":"right","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_4_0":{"textContent":"4","textAlign":"center","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_4_1":{"textContent":"You","textAlign":"left","verticalAlign":"middle","fontSize":14,"textOpacity":1},"cell_4_2":{"textContent":"115","textAlign":"right","verticalAlign":"middle","fontSize":14,"textOpacity":1}},"col_0_width":50,"col_1_width":268,"col_2_width":150}
  if (jsonData.type === 'wireframe' && jsonData.wireframeName === 'table') {
    return { ...jsonData, type: 'table', wireframeName: undefined };
  }
}
